/**
 * Brother P-touch im Rastermodus -- ohne Treiber, direkt ueber USB.
 *
 * Grundlage ist Brothers „Software Developer's Manual -- Raster Command
 * Reference PT-E550W/P750W/P710BT" (Version 1.02). Die Werte hier stehen dort
 * in Abschnitt 2.3 und 4; wer sie aendert, sollte dort nachsehen.
 *
 * Diese Datei ist **rein**: Sie baut Bytes aus einem Bild und liest Bytes der
 * Statusantwort. Wie die Bytes zum Geraet kommen, steht in `webusb.ts`. So
 * laesst sich das Protokoll ohne Drucker pruefen.
 */

/** Aufloesung in Punkt je Zoll, quer und laengs (180 × 180 dpi). */
export const DPI = 180;

/** Millimeter in Druckpunkte. */
export const punkte = (mm: number): number => Math.round((mm / 25.4) * DPI);

/** Die Bandbreiten, die der PT-E550W kann (TZe). */
export type Bandbreite = 6 | 9 | 12 | 18 | 24;
export const BANDBREITEN: readonly Bandbreite[] = [6, 9, 12, 18, 24];

/**
 * Bedruckbare Hoehe und Lage auf dem 128-Punkt-Kopf je Band (Handbuch 2.3.5).
 * `versatz` ist die Zahl der Pins, die am Rand frei bleiben -- auf beiden
 * Seiten gleich.
 */
export const BAENDER: Record<Bandbreite, { druckPunkte: number; versatz: number }> = {
  6: { druckPunkte: 32, versatz: 48 },
  9: { druckPunkte: 50, versatz: 39 },
  12: { druckPunkte: 70, versatz: 29 },
  18: { druckPunkte: 112, versatz: 8 },
  24: { druckPunkte: 128, versatz: 0 },
};

const KOPF_PINS = 128;
const BYTES_JE_ZEILE = KOPF_PINS / 8;

/** Ein Schwarz-Weiss-Bild: `punkte[y * breite + x]` ist 1 fuer schwarz. */
export interface Bild {
  breite: number;
  hoehe: number;
  punkte: Uint8Array;
}

export interface Auftragsoptionen {
  /** Jedes Etikett einzeln abschneiden. Vorgabe: ja. */
  abschneiden?: boolean;
  /** Vor- und Nachlauf je Etikett in Punkt. Vorgabe: 2 mm (14 Punkt), das Minimum. */
  rand?: number;
}

const ESC = 0x1b;

/**
 * Der vollstaendige Druckauftrag fuer ein oder mehrere Etiketten.
 *
 * Aufbau nach Handbuch 2.1: einmal Initialisieren, dann je Etikett
 * Steuercodes, Rasterzeilen und der Druckbefehl -- `0C` zwischen den
 * Etiketten, `1A` (drucken und vorschieben) nach dem letzten.
 *
 * Unkomprimiert: Ueber USB beginnt der Drucker dann schon waehrend der
 * Uebertragung (Handbuch 1, „concurrent printing"), und es gibt eine
 * Fehlerquelle weniger.
 */
export function druckauftrag(
  bilder: readonly Bild[],
  band: Bandbreite,
  optionen: Auftragsoptionen = {},
): Uint8Array<ArrayBuffer> {
  const { druckPunkte, versatz } = BAENDER[band];
  const abschneiden = optionen.abschneiden ?? true;
  const rand = optionen.rand ?? 14;
  const teile: number[] = [];

  // Initialisieren: 100 Byte „invalidate", dann ESC @ (Handbuch 2.1 (1)).
  for (let i = 0; i < 100; i += 1) teile.push(0x00);
  teile.push(ESC, 0x40);

  bilder.forEach((bild, seite) => {
    if (bild.hoehe !== druckPunkte) {
      throw new Error(
        `Das Etikett ist ${String(bild.hoehe)} Punkte hoch, das ${String(band)}-mm-Band braucht ${String(druckPunkte)}`,
      );
    }

    // Rastermodus.
    teile.push(ESC, 0x69, 0x61, 0x01);
    // Druckinformation: gueltig sind Breite und „immer wiederherstellen";
    // Breite in mm, Laenge 0 (Endlosband), Zahl der Rasterzeilen, erste Seite?
    const zeilen = bild.breite;
    teile.push(
      ESC,
      0x69,
      0x7a,
      0x84,
      0x00,
      band,
      0x00,
      zeilen & 0xff,
      (zeilen >> 8) & 0xff,
      (zeilen >> 16) & 0xff,
      (zeilen >> 24) & 0xff,
      seite === 0 ? 0x00 : 0x01,
      0x00,
    );
    // Verschiedene Modi: automatisch abschneiden (Bit 6).
    teile.push(ESC, 0x69, 0x4d, abschneiden ? 0x40 : 0x00);
    // Jedes Etikett einzeln schneiden.
    teile.push(ESC, 0x69, 0x41, 0x01);
    // Erweiterte Modi: keine Kettendruckung -- nach dem letzten vorschieben
    // und schneiden (Bit 3).
    teile.push(ESC, 0x69, 0x4b, 0x08);
    // Rand in Punkt.
    teile.push(ESC, 0x69, 0x64, rand & 0xff, (rand >> 8) & 0xff);
    // Keine Kompression.
    teile.push(0x4d, 0x00);

    // Je Spalte des Bildes eine Rasterzeile quer ueber den Kopf. Pin
    // `versatz + y` traegt die Bildzeile y; Pin 0 ist das hoechste Bit des
    // ersten Bytes (Handbuch 2.3.5).
    for (let x = 0; x < bild.breite; x += 1) {
      const zeile = new Uint8Array(BYTES_JE_ZEILE);
      for (let y = 0; y < bild.hoehe; y += 1) {
        if (bild.punkte[y * bild.breite + x] === 1) {
          const pin = versatz + y;
          zeile[pin >> 3] = (zeile[pin >> 3] ?? 0) | (0x80 >> (pin & 7));
        }
      }
      teile.push(0x47, BYTES_JE_ZEILE, 0x00, ...zeile);
    }

    teile.push(seite === bilder.length - 1 ? 0x1a : 0x0c);
  });

  return Uint8Array.from(teile);
}

/** Erst ESC @, dann ESC i S -- so fragt man nach dem Stand (Handbuch 4). */
export const STATUS_ANFRAGE = Uint8Array.from([
  ...new Array<number>(100).fill(0x00),
  ESC,
  0x40,
  ESC,
  0x69,
  0x53,
]);

export type StatusArt =
  "antwort" | "fertig" | "fehler" | "ausgeschaltet" | "hinweis" | "phase" | "unbekannt";

export interface DruckerStatus {
  art: StatusArt;
  /** Eingelegtes Band in mm; 0 = kein Band. */
  bandMm: number;
  /** Fehler in Worten, leer wenn alles gut ist. */
  fehler: string[];
  /** Druckt der Drucker gerade? */
  druckt: boolean;
}

const STATUS_ARTEN: Record<number, StatusArt> = {
  0x00: "antwort",
  0x01: "fertig",
  0x02: "fehler",
  0x04: "ausgeschaltet",
  0x05: "hinweis",
  0x06: "phase",
};

/** Liest die 32 Byte Statusantwort (Handbuch 4, „Status information request"). */
export function statusLesen(b: Uint8Array): DruckerStatus {
  if (b.length < 32 || b[0] !== 0x80 || b[1] !== 0x20 || b[2] !== 0x42) {
    throw new Error("Das ist keine Statusantwort eines Brother-Druckers");
  }
  const f1 = b[8] ?? 0;
  const f2 = b[9] ?? 0;
  const fehler: string[] = [];
  if (f1 & 0x01) fehler.push("Kein Band eingelegt");
  if (f1 & 0x04) fehler.push("Das Messer klemmt");
  if (f1 & 0x08) fehler.push("Der Akku ist fast leer");
  if (f2 & 0x01) fehler.push("Falsches Band eingelegt");
  if (f2 & 0x10) fehler.push("Die Klappe ist offen");
  if (f2 & 0x20) fehler.push("Der Drucker ist zu heiß");
  return {
    art: STATUS_ARTEN[b[18] ?? 0xff] ?? "unbekannt",
    bandMm: b[10] ?? 0,
    fehler,
    druckt: (b[19] ?? 0) === 0x01,
  };
}

/** Die Bandbreite zu einer Statusangabe -- 3,5 mm meldet der Drucker als 4. */
export function bandAusStatus(mm: number): Bandbreite | null {
  return (BANDBREITEN as readonly number[]).includes(mm) ? (mm as Bandbreite) : null;
}
